const isFiniteNumber = (value) => typeof value === 'number' && isFinite(value);
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

class TourUpdateManager {
  constructor(socket = null) {
    this.lastCameraUpdate = 0;
    this.lastPoseUpdate = 0;
    this.cameraUpdateInterval = 1000 / 15;
    this.poseUpdateInterval = 1000 / 10;
    this.userId = null;
    this.socket = socket;
    this.smoothingEnabled = true;
    this.smoother = null;
  }

  setSocket(socket) {
    this.socket = socket;
  }

  setUserId(userId) {
    this.userId = userId;
  }

  updateCamera(position, rotation, fov, mouse) {
    const now = Date.now();
    if (now - this.lastCameraUpdate < this.cameraUpdateInterval) {
      return false;
    }

    const posArray = Array.isArray(position) ? position : [position.x, position.y, position.z];
    if (!posArray.every(isFiniteNumber)) {
      console.warn('TourUpdateManager: Invalid position data detected:', posArray);
      return false;
    }

    const rotArray = Array.isArray(rotation) ? rotation : [rotation.x, rotation.y, rotation.z];
    if (!rotArray.every(isFiniteNumber)) {
      console.warn('TourUpdateManager: Invalid rotation data detected:', rotArray);
      return false;
    }

    if (!isFiniteNumber(fov)) {
      console.warn('TourUpdateManager: Invalid FOV detected:', fov);
      return false;
    }



    this.lastCameraUpdate = now;
    if (this.socket && this.userId) {
      this.socket.emit('tour-camera-update', {
        userId: this.userId,
        position: posArray,
        rotation: rotArray,
        fov: clamp(fov, 1, 180),
        mouse
      });
      return true;
    }
    return false;
  }

  updatePose(position, quaternion) {
    const now = Date.now();
    if (now - this.lastPoseUpdate < this.poseUpdateInterval) {
      return false;
    }

    const posArray = Array.isArray(position) ? position : [position.x, position.y, position.z];
    if (!posArray.every(isFiniteNumber)) {
      return false;
    }

    const quatArray = Array.isArray(quaternion) ? quaternion : [quaternion.x, quaternion.y, quaternion.z, quaternion.w];
    if (!quatArray.every(isFiniteNumber)) {
      return false;
    }

    this.lastPoseUpdate = now;
    if (this.socket && this.userId) {
      this.socket.emit('tour-pose-update', {
        userId: this.userId,
        pos: posArray,
        quat: quatArray
      });
      return true;
    }
    return false;
  }

  handleIncomingCameraUpdate(payload, model) {
    if (!model || !model.camera) return;





    // Handle cursor positioning if provided
    if (payload.mouse) {
      const cursor = $("#follow-cursor");
      if (cursor[0] && isFiniteNumber(payload.mouse.x) && isFiniteNumber(payload.mouse.y)) {
        const normalizedX = payload.mouse.x;
        const normalizedY = payload.mouse.y;
        const otherScreenWidth = window.innerWidth;
        const otherScreenHeight = window.innerHeight;
        const posX = normalizedX * otherScreenWidth;
        const posY = normalizedY * otherScreenHeight;

        if (isFiniteNumber(posX) && isFiniteNumber(posY)) {
          cursor[0].style.left = posX + 'px';
          cursor[0].style.top = posY + 'px';
          cursor.show();
        }
      }
    }

    // Apply camera updates with optional smoothing
    const hasValidPosition = payload.position && Array.isArray(payload.position) && payload.position.length >= 3 &&
      isFiniteNumber(payload.position[0]) && isFiniteNumber(payload.position[1]) && isFiniteNumber(payload.position[2]);

    const hasValidRotation = payload.rotation && Array.isArray(payload.rotation) && payload.rotation.length >= 3 &&
      isFiniteNumber(payload.rotation[0]) && isFiniteNumber(payload.rotation[1]) && isFiniteNumber(payload.rotation[2]);

    const hasValidFov = payload.fov !== undefined && isFiniteNumber(payload.fov);

    if (this.smoothingEnabled) {
      if (!this.smoother) {
        this.smoother = new SmoothCameraRenderer();
        this.smoother.currentPosition = [model.camera.position.x, model.camera.position.y, model.camera.position.z];
        this.smoother.currentRotation = [model.camera.rotation.x, model.camera.rotation.y, model.camera.rotation.z];
        this.smoother.currentFov = model.camera.fov;
        this.smoother.lerpSpeed = 0.18;

        this.smoother.setRenderCallback((pos, rot, fov) => {
          if (!model || !model.camera) return;
          model.camera.position.set(pos[0], pos[1], pos[2]);
          model.camera.rotation.set(rot[0], rot[1], rot[2]);
          if (isFiniteNumber(fov)) {
            model.camera.fov = fov;
          }
          if (model.view && model.view.controls) {
            model.view.controls.update();
          }
          model.camera.updateProjectionMatrix();
          if (model.view && model.view.updateShphereTextureOnMove) {
            model.view.updateShphereTextureOnMove();
          }
        });
        this.smoother.start();
      }

      const target = {};
      if (hasValidPosition) target.position = payload.position;
      if (hasValidRotation) target.rotation = payload.rotation;
      if (hasValidFov) target.fov = clamp(payload.fov, 1, 180);
      this.smoother.onCameraUpdate(target);
    } else {
      if (hasValidPosition) {
        model.camera.position.x = payload.position[0];
        model.camera.position.y = payload.position[1];
        model.camera.position.z = payload.position[2];
      }

      if (hasValidRotation) {
        model.camera.rotation.set(payload.rotation[0], payload.rotation[1], payload.rotation[2]);
      }

      if (hasValidFov) {
        const clampedFov = clamp(payload.fov, 1, 180);
        model.camera.fov = clampedFov;
      }

      if (model.view && model.view.controls) {
        model.view.controls.update();
      }
      model.camera.updateProjectionMatrix();

      if (model.view && model.view.updateShphereTextureOnMove) {
        model.view.updateShphereTextureOnMove();
      }
    }
  }

  stopSmoothing() {
    if (this.smoother) {
      this.smoother.stop();
      this.smoother = null;
    }
  }
}

class DeltaUpdateManager {
  constructor() {
    this.lastPosition = null;
    this.lastRotation = null;
    this.positionThreshold = 0.01;
    this.rotationThreshold = 0.05;
  }

  shouldUpdateCamera(newPos, newRot) {
    if (!this.lastPosition || !this.lastRotation) {
      return true;
    }

    const posChanged = this.vectorDistance(newPos, this.lastPosition) > this.positionThreshold;
    const rotChanged = this.vectorDistance(newRot, this.lastRotation) > this.rotationThreshold;

    return posChanged || rotChanged;
  }

  updateCamera(position, rotation, fov, tourUpdateManager) {
    const posArray = Array.isArray(position) ? position : [position.x, position.y, position.z];
    const rotArray = Array.isArray(rotation) ? rotation : [rotation.x, rotation.y, rotation.z];

    if (this.shouldUpdateCamera(posArray, rotArray)) {
      const updated = tourUpdateManager.updateCamera(posArray, rotArray, fov);
      if (updated) {
        this.lastPosition = [...posArray];
        this.lastRotation = [...rotArray];
      }
      return updated;
    }
    return false;
  }

  vectorDistance(a, b) {
    const dx = a[0] - b[0];
    const dy = a[1] - b[1];
    const dz = a[2] - b[2];
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  reset() {
    this.lastPosition = null;
    this.lastRotation = null;
  }
}

class SmoothCameraRenderer {
  constructor() {
    this.targetPosition = [0, 0, 0];
    this.targetRotation = [0, 0, 0];
    this.currentPosition = [0, 0, 0];
    this.currentRotation = [0, 0, 0];
    this.currentFov = 75;
    this.targetFov = 75;
    this.lerpSpeed = 0.1;
    this.isActive = false;
    this.renderCallback = null;
    this._rafId = null;
    this._boundTick = () => this._tick();
  }

  onCameraUpdate(data) {
    this.targetPosition = data.position || this.targetPosition;
    this.targetRotation = data.rotation || this.targetRotation;
    if (typeof data.fov === 'number' && isFinite(data.fov)) {
      this.targetFov = data.fov;
    }
  }

  update() {
    if (!this.isActive) return;

    this.currentPosition = this.lerp3D(this.currentPosition, this.targetPosition, this.lerpSpeed);
    this.currentRotation = this.lerp3D(this.currentRotation, this.targetRotation, this.lerpSpeed);
    this.currentFov = this.lerp1D(this.currentFov, this.targetFov, this.lerpSpeed);

    if (this.renderCallback) {
      this.renderCallback(this.currentPosition, this.currentRotation, this.currentFov);
    }
  }

  lerp3D(current, target, factor) {
    return [
      current[0] + (target[0] - current[0]) * factor,
      current[1] + (target[1] - current[1]) * factor,
      current[2] + (target[2] - current[2]) * factor
    ];
  }

  lerp1D(current, target, factor) {
    return current + (target - current) * factor;
  }

  setRenderCallback(callback) {
    this.renderCallback = callback;
  }

  _tick() {
    if (!this.isActive) return;
    this.update();
    this._rafId = requestAnimationFrame(this._boundTick);
  }

  start() {
    if (this.isActive) return;
    this.isActive = true;
    this._rafId = requestAnimationFrame(this._boundTick);
  }

  stop() {
    this.isActive = false;
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }
}

export { TourUpdateManager, DeltaUpdateManager, SmoothCameraRenderer };